import { useCallback, useEffect, useState } from 'react'
import { ExternalLink, RotateCcw } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent } from '@orisini/ui/components/card'
import { api, ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { type PinDto } from '@/features/pins/api'
import { PIN_STATUS_LABEL } from '@/features/pins/constants'

interface AttemptDto {
  attemptNo: number
  state: string
  startedAt: number | null
  finishedAt: number | null
  durationMs: number | null
  lastStep: string | null
  errorCode: string | null
  errorMessage: string | null
  screenshotKey: string | null
  resultPinUrl: string | null
}

interface EventDto {
  id: string
  level: string
  code: string
  message: string | null
  createdAt: number
}

export default function HistoryPage() {
  const [tab, setTab] = useState<'pins' | 'events'>('pins')
  const [pins, setPins] = useState<PinDto[]>([])
  const [events, setEvents] = useState<EventDto[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [attempts, setAttempts] = useState<Record<string, AttemptDto[]>>({})
  const [lightbox, setLightbox] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [pinsRes, eventsRes] = await Promise.all([
        api.get<{ pins: PinDto[] }>('/pins?status=posted,failed,cancelled&limit=100'),
        api.get<{ events: EventDto[] }>('/logs?limit=100'),
      ])
      setPins(pinsRes.pins)
      setEvents(eventsRes.events)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t load history')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function toggleAttempts(pinId: string) {
    if (expanded === pinId) {
      setExpanded(null)
      return
    }
    setExpanded(pinId)
    if (!attempts[pinId]) {
      try {
        const res = await api.get<{ attempts: AttemptDto[] }>(`/logs/attempts/${pinId}`)
        setAttempts((a) => ({ ...a, [pinId]: res.attempts }))
      } catch {
        /* biar kosong */
      }
    }
  }

  async function retry(pin: PinDto) {
    try {
      await api.post(`/pins/${pin.id}/retry`)
      toast.success('Pin added back to the queue')
      await load()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t retry')
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">History</h1>
        <p className="text-sm text-muted-foreground">Published & failed pins, plus event log</p>
      </header>

      <div className="flex gap-1 rounded-md bg-muted p-1 w-fit">
        {(
          [
            ['pins', `Pins (${pins.length})`],
            ['events', `Event log (${events.length})`],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              'rounded px-4 py-1.5 text-sm transition-colors',
              tab === id ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'pins' && (
        <div className="space-y-2">
          {pins.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No history yet.
              </CardContent>
            </Card>
          )}
          {pins.map((pin) => {
            const status = PIN_STATUS_LABEL[pin.status] ?? PIN_STATUS_LABEL.draft
            const list = attempts[pin.id]
            return (
              <Card key={pin.id}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="size-12 shrink-0 overflow-hidden rounded bg-muted">
                      {pin.media?.previewUrl && (
                        <img src={pin.media.previewUrl} alt="" className="size-full object-cover" loading="lazy" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => void toggleAttempts(pin.id)}
                          className="truncate text-left text-sm font-medium hover:underline"
                        >
                          {pin.title || pin.description || '(untitled)'}
                        </button>
                        <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-xs', status.className)}>
                          {status.text}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {pin.postedAt
                          ? `published ${new Date(pin.postedAt).toLocaleString('en-GB')}`
                          : pin.lastErrorMessage || pin.lastErrorCode || ''}
                      </p>
                    </div>
                    {pin.pinterestPinUrl && (
                      <a
                        href={pin.pinterestPinUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="flex shrink-0 items-center gap-1 text-xs text-foreground hover:underline"
                      >
                        View pin <ExternalLink className="size-3" />
                      </a>
                    )}
                    {pin.status === 'failed' && (
                      <Button variant="outline" size="sm" onClick={() => void retry(pin)}>
                        <RotateCcw />
                        Try again
                      </Button>
                    )}
                  </div>

                  {expanded === pin.id && (
                    <div className="mt-3 space-y-2 border-t pt-3">
                      {!list && <p className="text-xs text-muted-foreground">Loading attempts…</p>}
                      {list?.length === 0 && (
                        <p className="text-xs text-muted-foreground">No attempts recorded yet.</p>
                      )}
                      {list?.map((a) => (
                        <div key={a.attemptNo} className="flex items-center gap-3 text-xs">
                          <span className="w-20 shrink-0 text-muted-foreground">
                            Attempt {a.attemptNo}
                          </span>
                          <span
                            className={cn(
                              'shrink-0 rounded px-1.5 py-0.5',
                              a.state === 'succeeded'
                                ? 'bg-emerald-500/15 text-emerald-400'
                                : a.state === 'running'
                                  ? 'bg-sky-500/15 text-sky-400'
                                  : 'bg-destructive/15 text-destructive',
                            )}
                          >
                            {a.state}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-muted-foreground">
                            {a.errorCode ? `${a.errorCode}: ${a.errorMessage ?? ''}` : a.lastStep ?? ''}
                            {a.durationMs ? ` · ${Math.round(a.durationMs / 100) / 10}s` : ''}
                          </span>
                          {a.screenshotKey && (
                            <button
                              className="shrink-0 text-foreground hover:underline"
                              onClick={() =>
                                setLightbox(`/api/logs/artifact?key=${encodeURIComponent(a.screenshotKey!)}`)
                              }
                            >
                              Screenshot
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {tab === 'events' && (
        <Card>
          <CardContent className="divide-y p-0">
            {events.map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-2 text-xs">
                <span
                  className={cn(
                    'w-12 shrink-0 font-medium',
                    e.level === 'error'
                      ? 'text-destructive'
                      : e.level === 'warn'
                        ? 'text-amber-500'
                        : 'text-muted-foreground',
                  )}
                >
                  {e.level}
                </span>
                <span className="w-44 shrink-0 truncate text-muted-foreground">{e.code}</span>
                <span className="min-w-0 flex-1 truncate">{e.message}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {new Date(e.createdAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {lightbox && (
        <button
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-8"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="Failure screenshot" className="max-h-full max-w-full rounded-lg" />
        </button>
      )}
    </div>
  )
}
